import { IActionFn, IOptionRecord, IPageData } from '@/store/types';
import { IModuleState } from './types';
import { $http } from '@/axios';
import store from '@/store';

// 查询当前组织下的数字资产
export const queryDigitalAssets: IActionFn<IModuleState> = async ({ commit, state }, payload) => {
  try {
    const isAdvanceSearch = !!payload.conditions;
    const postUrl = isAdvanceSearch ? '/v1/asset/digitalasset/advsearch' : '/v1/asset/digitalasset/search';
    commit('setDigitalAssets', { ...state.list, loading: true });
    const res: IPageData = await $http.post(postUrl, payload);
    commit('setDigitalAssets', { data: res.list, pageNum: res.pageNum, pageSize: res.pageSize, total: res.total });
  } catch (e) {
    commit('setDigitalAssets', { data: [], pageNum: 0, pageSize: 0, total: 0 });
  }
};

// 查询数字资产详情
export const queryDigitalAssetDetail: IActionFn<IModuleState> = async ({ commit, state }, payload) => {
  try {
    const { code, keep = false } = payload;
    const initState = keep ? { ...state.detail, loading: true } : { data: null, loading: true };
    commit('setDigitalAssetDetail', initState);
    const res = await $http.get(`/v1/asset/digitalasset/${code}/detail`);
    commit('setDigitalAssetDetail', { data: res });
    return res;
  } catch (e) {
    commit('setDigitalAssetDetail', { data: null });
  }
};

// 查询所有可用的类目列表(平台查询自己组织的类目列表，企业查询自己组织的类目列表 + 系统已发布的类目列表)
export const queryAllDefsByOrgType: IActionFn<IModuleState> = async ({ commit, state }, isPlatform) => {
  commit('setAllDefsByOrgType', { ...state.allDefs, loading: true });

  if (isPlatform) {
    // 如果是平台用户，则只查询当前组织的类目列表
    $http.post('/v1/asset/digitaldef/list', {}).then((res) => {
      commit('setAllDefsByOrgType', { data: res });
    }).catch(() => {
      commit('setAllDefsByOrgType', { data: [] });
    });
  } else {
    // 如果是企业用户，则查询当前组织的类目列表 和 查询系统已发布的类目列表
    const fetchDefsByCurOrg = $http.post('/v1/asset/digitaldef/list', {});
    const fetchDefsByPublished = $http.post('/v1/asset/digitaldef/publishedsyslist', {});
    Promise.all([fetchDefsByCurOrg, fetchDefsByPublished]).then((results) => {
      const orgDefs = (results[0] || []) as unknown as any[];
      const sysDefs = (results[1] || []) as unknown as any[];
      commit('setAllDefsByOrgType', { data: [...orgDefs, ...sysDefs] });
    }).catch(() => {
      commit('setAllDefsByOrgType', { data: [] });
    });
  }
};

// 检查数字资产编码是否可用
export const checkDigitalAssetExisted: IActionFn<IModuleState> = async ({ commit, state }, code) => {
  try {
    commit('setDigitalAssetExisted', { data: null, loading: true });
    const res = await $http.post('/v1/asset/digitalasset/codeexistence', { code });
    commit('setDigitalAssetExisted', { data: !!res });
  } catch (e) {
    commit('setDigitalAssetExisted', { data: false });
  }
};

// 检查数字资产文件是否存在
export const checkFileExisted: IActionFn<IModuleState> = async ({ commit, state }, payload) => {
  return new Promise((resolve, reject) => {
    $http.post<boolean>('/v1/asset/digitalasset/hasfile', payload).then((hasFile) => {
      if (hasFile) {
        resolve();
      } else {
        store.commit('common/setToast', { visible: true, text: '数字资产文件不存在' });
        reject();
      }
    }).catch((e) => {
      reject(e);
    });
  });
};

// 根据类目 id 查询类目的属性列表
export const queryDigitalAttrsByDef: IActionFn<IModuleState> = async ({ commit, state }, payload) => {
  try {
    commit('setDigitalAttrsByDef', { data: [], loading: true });
    const res = await $http.post('/v1/data/asset/digitalasset/attributes', payload);
    commit('setDigitalAttrsByDef', { data: res });
  } catch (e) {
    commit('setDigitalAttrsByDef', { data: [] });
  }
};

// 查询检索条件类型列表
export const queryConditionTypes: IActionFn<IModuleState> = ({ commit, state }) => {
  const fetchStringConditionTypes = $http.get('/common/chooseoptions/StringConditionType');
  const fetchNumberConditionTypes = $http.get('/common/chooseoptions/ValueConditionType');
  const fetchSelectConditionTypes = $http.get('/common/chooseoptions/SelectConditionType');
  const promises: any[] = [fetchStringConditionTypes, fetchNumberConditionTypes, fetchSelectConditionTypes];

  commit('setConditionTypes', { ...state.conditionTypes, loading: true });
  Promise.all<IOptionRecord[]>(promises).then(([strTypes, numTypes, selectTypes]) => {
    // 属性类型为'文本和日期'时，使用字符串条件
    const strTypeList = strTypes.map((item) => {
      const { optionKey, optionValue } = item;
      return { optionKey, optionValue, applyToAttrTypes: [1, 3] };
    });

    // 属性类型为'数值'时，使用数值类条件
    const numTypeList = numTypes.map((item) => {
      const { optionKey, optionValue } = item;
      return { optionKey, optionValue, applyToAttrTypes: [2] };
    });

    // 属性类型为'单选和多选'时，使用选项类条件
    const selectTypeList = selectTypes.map((item) => {
      const { optionKey, optionValue } = item;
      return { optionKey, optionValue, applyToAttrTypes: [11, 12] };
    });

    const data = {
      StringConditionType: strTypeList,
      ValueConditionType: numTypeList,
      SelectConditionType: selectTypeList,
    };

    commit('setConditionTypes', { data });
  }).catch(() => {
    commit('setConditionTypes', { data: [] });
  });
};

// 查询文件类型类别
export const queryDigitalFileTypes: IActionFn<IModuleState> = async ({ commit, state }) => {
  try {
    commit('setDigitalFileTypes', { ...state.fileTypes, loading: true });
    const res = await $http.get('/common/chooseoptions/DigitalAssetFileType');
    commit('setDigitalFileTypes', { data: res });
  } catch (e) {
    commit('setDigitalFileTypes', { data: [] });
  }
};

// 获取文件上传时需要的验证信息
export const queryUploadPolicy: IActionFn<IModuleState> = async ({ commit, state }, payload) => {
  try {
    commit('setUploadPolicy', { data: null, loading: true });
    const res = await $http.post('/v1/asset/digitalasset/uploaddetailfile', payload);
    commit('setUploadPolicy', { data: res });
    return res;
  } catch (e) {
    commit('setUploadPolicy', { data: null });
  }
};

// 新增数字资产
export const addDigitalAsset: IActionFn<IModuleState> = async ({ commit, state }, payload) => {
  try {
    commit('setLoading', true);
    const res = await $http.post('/v1/asset/digitalasset/add', payload);
    store.commit('common/setToast', { visible: true, text: '新增成功' });
    return res;
  } finally {
    commit('setLoading', false);
  }
};

// 删除数字资产
export const deleteDigitalAsset: IActionFn<IModuleState> = async ({ commit, state }, payload) => {
  try {
    commit('setConfirm', { ...state.confirm, loading: true });
    await $http.post('/v1/asset/digitalasset/delete', payload);
    store.commit('common/setToast', { visible: true, text: '删除成功' });
  } finally {
    commit('setConfirm', { visible: false, loading: false });
  }
};

// 编辑数字资产
export const editDigitalAsset: IActionFn<IModuleState> = async ({ commit, state }, payload) => {
  try {
    commit('setLoading', true);
    await $http.post('/v1/asset/digitalasset/update', payload);
    store.commit('common/setToast', { visible: true, text: '编辑成功' });
  } finally {
    commit('setLoading', false);
  }
};

// 新增数字资产详情(添加文件相关信息)
export const addDigitalAssetDetail: IActionFn<IModuleState> = async ({ commit, state }, payload) => {
  try {
    commit('setDialog', { ...state.dialog, loading: true });
    await $http.post('/v1/asset/digitalasset/adddetails', payload);
  } finally {
    commit('setDialog', { visible: false });
  }
};

// 编辑数字资产详情(编辑文件相关信息)
export const editDigitalAssetDetail: IActionFn<IModuleState> = async ({ commit, state }, payload) => {
  try {
    commit('setDialog', { ...state.dialog, loading: true });
    await $http.post('/v1/asset/digitalasset/modifydetail', payload);
  } finally {
    commit('setDialog', { visible: false });
  }
};

// 删除数字资产明细
export const deleteDigitalAssetDetail: IActionFn<IModuleState> = async ({ commit, state }, payload) => {
  try {
    commit('setConfirm', { ...state.confirm, loading: true });
    await $http.post('/v1/asset/digitalasset/deletedetails', payload);
    store.commit('common/setToast', { visible: true, text: '删除成功' });
  } finally {
    commit('setConfirm', { visible: false, loading: false });
  }
};

// 根据资产编码，查询资产的变更日志
export const queryAssetLogsByCode: IActionFn<IModuleState> = async ({ commit, state }, payload) => {
  try {
    const { code, ...rest } = payload;
    commit('setAssetLogsByCode', { ...state.assetLogsByCode, loading: true });
    const res: IPageData = await $http.post(`/v1/asset/digitalasset/${code}/updatelog`, rest);
    commit('setAssetLogsByCode', { data: res.list, pageNum: res.pageNum, pageSize: res.pageSize, total: res.total });
  } catch (e) {
    commit('setAssetLogsByCode', { data: [], pageNum: 0, pageSize: 0, total: 0 });
  }
};

// 根据类目ID，查询资产的变更日志
export const queryAssetLogsByDef: IActionFn<IModuleState> = async ({ commit, state }, payload) => {
  try {
    const { defId, ...rest } = payload;
    commit('setAssetLogsByDef', { ...state.assetLogsByDef, loading: true });
    const res: IPageData = await $http.post(`/v1/asset/digitalasset/${defId}/digitalassetupdatelog`, rest);
    commit('setAssetLogsByDef', { data: res.list, pageNum: res.pageNum, pageSize: res.pageSize, total: res.total });
  } catch (e) {
    commit('setAssetLogsByDef', { data: [], pageNum: 0, pageSize: 0, total: 0 });
  }
};
